import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiSearch,
  FiPlusCircle,
  FiPlay,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiPause,
  FiStopCircle,
  FiSkipForward,
  FiSkipBack,
  FiShuffle,
  FiRepeat,
  FiClock,
  FiHeart,
  FiList,
  FiVolume2,
  FiVolumeX,
  FiCheck,
} from 'react-icons/fi';
import { inject, observer } from 'mobx-react';
import { RingLoader } from 'react-spinners';
import ApiStore from '../../../api/ApiStore';

// Constants
const COVER_IMAGES = [
  '/podcast_cover_1.jpg',
  '/podcast_cover_4_clean.jpg',
  '/podcast_cover_2.jpg',
];

const CATEGORIES = ['Todos', 'Autoescuela', 'Personalizado'];
const MAX_RETRIES = 3;
const WELCOME_PODCAST_ID = 29;
const ANIMATION_DURATION = 300; // ms

// Types
interface Podcast {
  id: number;
  image: string;
  category: string;
  title: string;
  topic: string;
  file_name: string;
  description: string;
  signed_url: string;
  duration: number;
}

interface PodcastListProps {
  store: any;
}

// Helper functions
const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const hDisplay = h > 0 ? `${h}:` : '';
  const mDisplay = m < 10 && h > 0 ? `0${m}:` : `${m}:`;
  const sDisplay = s < 10 ? `0${s}` : s;
  return `${hDisplay}${mDisplay}${sDisplay}`;
};

// Async function with retry logic
const fetchWithRetry = async <T,>(
  fetchFn: () => Promise<T>,
  maxRetries = MAX_RETRIES
): Promise<T> => {
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      return await fetchFn();
    } catch (err) {
      retryCount++;
      if (retryCount >= maxRetries) {
        throw err;
      }
      // Wait before retrying (exponential backoff)
      await new Promise(resolve =>
        setTimeout(resolve, 1000 * Math.pow(2, retryCount))
      );
    }
  }

  throw new Error('Max retries exceeded');
};

// Component for the mini-player that appears at the bottom when playing
const MiniPlayer = ({
  currentPodcast,
  isPlaying,
  progress,
  togglePlayPause,
  toggleSidebar,
}) => {
  if (!currentPodcast) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-3 flex items-center shadow-lg z-30 cursor-pointer"
      onClick={toggleSidebar}
    >
      <img
        src={currentPodcast.image}
        alt={currentPodcast.title}
        className="h-12 w-12 rounded-md object-cover mr-4 shadow-md"
      />
      <div className="flex-grow mr-4">
        <h4 className="font-medium text-sm truncate">{currentPodcast.title}</h4>
        <div className="w-full bg-white bg-opacity-20 h-1 rounded-full mt-1">
          <div
            className="bg-white h-1 rounded-full"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
      <button
        onClick={e => {
          e.stopPropagation();
          togglePlayPause();
        }}
        className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <FiPause className="text-white" />
        ) : (
          <FiPlay className="text-white" />
        )}
      </button>
    </div>
  );
};

// Main podcast list component
const PodcastList = inject('store')(
  observer(({ store }: PodcastListProps) => {
    // State management
    const [searchTerm, setSearchTerm] = useState('');
    const [playlist, setPlaylist] = useState<Podcast[]>([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isShuffle, setIsShuffle] = useState(false);
    const [isRepeat, setIsRepeat] = useState(false);
    const [podcasts, setPodcasts] = useState<Podcast[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(
      null
    );
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [volume, setVolume] = useState(80);
    const [isMuted, setIsMuted] = useState(false);
    const [activeCategory, setActiveCategory] = useState('Todos');
    const [hoveredPodcast, setHoveredPodcast] = useState<number | null>(null);
    const [miniPlayerVisible, setMiniPlayerVisible] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    // Refs
    const audioRef = useRef(new Audio());
    const lastPlayedTrackRef = useRef<string | null>(null);
    const playbackUrlRef = useRef('');
    const shouldPlayRef = useRef(false);
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Toggle sidebar with transition animation
    const toggleSidebar = useCallback(() => {
      setIsSidebarOpen(prev => !prev);

      // Show mini player when sidebar is closed and something is playing
      if (isSidebarOpen && currentTrackIndex !== null) {
        setTimeout(() => {
          setMiniPlayerVisible(true);
        }, ANIMATION_DURATION);
      } else {
        setMiniPlayerVisible(false);
      }
    }, [isSidebarOpen, currentTrackIndex]);

    // Memoized functions
    const forcePlayCurrentTrack = useCallback(() => {
      lastPlayedTrackRef.current = null;
      shouldPlayRef.current = true;
    }, []);

    const handleStop = useCallback(() => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    }, []);

    const handleNext = useCallback(() => {
      if (playlist.length === 0) return;

      let nextIndex = currentTrackIndex !== null ? currentTrackIndex + 1 : 0;

      if (isShuffle) {
        nextIndex = Math.floor(Math.random() * playlist.length);
      } else if (nextIndex >= playlist.length) {
        if (isRepeat) {
          nextIndex = 0;
        } else {
          handleStop();
          return;
        }
      }

      setCurrentTrackIndex(nextIndex);

      // Force playback of the new track after a small delay
      setTimeout(() => {
        forcePlayCurrentTrack();
      }, 100);
    }, [
      currentTrackIndex,
      isShuffle,
      isRepeat,
      playlist.length,
      handleStop,
      forcePlayCurrentTrack,
    ]);

    const handlePrevious = useCallback(() => {
      if (playlist.length === 0) return;

      let prevIndex = currentTrackIndex !== null ? currentTrackIndex - 1 : 0;

      if (isShuffle) {
        prevIndex = Math.floor(Math.random() * playlist.length);
      } else if (prevIndex < 0) {
        if (isRepeat) {
          prevIndex = playlist.length - 1;
        } else {
          handleStop();
          return;
        }
      }

      setCurrentTrackIndex(prevIndex);

      // Force playback of the new track after a small delay
      setTimeout(() => {
        forcePlayCurrentTrack();
      }, 100);
    }, [
      currentTrackIndex,
      isShuffle,
      isRepeat,
      playlist.length,
      handleStop,
      forcePlayCurrentTrack,
    ]);

    const handlePlay = useCallback(() => {
      if (currentTrackIndex === null && playlist.length > 0) {
        setCurrentTrackIndex(0);
      } else {
        audioRef.current.play().catch(err => console.error('Play error:', err));
        setIsPlaying(true);
      }
    }, [currentTrackIndex, playlist.length]);

    const handlePause = useCallback(() => {
      audioRef.current.pause();
      setIsPlaying(false);
    }, []);

    const togglePlayPause = useCallback(() => {
      if (isPlaying) {
        handlePause();
      } else {
        handlePlay();
      }
    }, [isPlaying, handlePause, handlePlay]);

    const toggleShuffle = useCallback(() => {
      setIsShuffle(prev => !prev);
    }, []);

    const toggleRepeat = useCallback(() => {
      setIsRepeat(prev => !prev);
    }, []);

    const toggleMute = useCallback(() => {
      const audio = audioRef.current;
      if (isMuted) {
        audio.volume = volume / 100;
        setIsMuted(false);
      } else {
        audio.volume = 0;
        setIsMuted(true);
      }
    }, [isMuted, volume]);

    const handleVolumeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseInt(e.target.value);
        setVolume(newVolume);
        audioRef.current.volume = newVolume / 100;

        if (newVolume === 0) {
          setIsMuted(true);
        } else if (isMuted) {
          setIsMuted(false);
        }
      },
      [isMuted]
    );

    // Fetch podcasts
    useEffect(() => {
      const fetchPodcasts = async () => {
        try {
          // Get organization name from the store
          const organizationName = store.profile?.organization || 'RicoCorp';
          const applicationName = 'Virtus';

          setLoading(true);
          setError(null);

          // Fetch the welcome podcast
          const bienvenidaData = await fetchWithRetry(() =>
            ApiStore.getAudioSignedUrl(WELCOME_PODCAST_ID)
          );

          // Create the welcome podcast object
          const bienvenidaPodcast: Podcast = {
            id: WELCOME_PODCAST_ID,
            image: '/podcast_wellcome_cover.webp',
            category: 'Autoescuela',
            title: 'Bienvenida',
            topic: 'Bienvenida',
            file_name: 'Bienvenida',
            description: 'Podcast de introducción a la plataforma Virtus Auto.',
            signed_url: bienvenidaData.signed_url,
            duration: 0,
          };

          // Fetch user podcasts
          const fetchedPodcasts = await fetchWithRetry(() =>
            ApiStore.getUserPodcasts(organizationName, applicationName)
          );

          const podcastsWithCovers = fetchedPodcasts.map(podcast => ({
            ...podcast,
            id: podcast.file_id,
            image:
              COVER_IMAGES[Math.floor(Math.random() * COVER_IMAGES.length)],
            category: 'Personalizado',
            title: podcast.topic || 'Podcast Personalizado',
            topic: podcast.topic || 'Podcast Personalizado',
            file_name: podcast.file_name || `podcast_${podcast.file_id}`,
            description:
              'Podcast personalizado que revisa las preguntas más falladas.',
            signed_url: podcast.signed_url,
            duration: 0,
          }));

          // Combine podcasts
          const combinedPodcasts = [bienvenidaPodcast, ...podcastsWithCovers];
          setPodcasts(combinedPodcasts);
          setLoading(false);

          // Load durations for all podcasts
          combinedPodcasts.forEach((podcast, index) => {
            if (!podcast.signed_url) return;

            const audio = new Audio(podcast.signed_url);

            audio.addEventListener('loadedmetadata', () => {
              setPodcasts(prevPodcasts => {
                const updatedPodcasts = [...prevPodcasts];
                updatedPodcasts[index].duration = audio.duration;
                return updatedPodcasts;
              });
            });

            audio.addEventListener('error', e => {
              console.error(
                `Error loading podcast audio for ${podcast.title}:`,
                e
              );
            });
          });
        } catch (err) {
          console.error('Error fetching podcasts:', err);
          setError(
            err.message ||
              'Error al cargar los podcasts. Por favor, inténtalo de nuevo.'
          );
          setLoading(false);
        }
      };

      // Only fetch podcasts if user is logged in
      if (store.isLoggedIn) {
        fetchPodcasts();
      }
    }, [store.isLoggedIn, store.profile?.organization]);

    // Set initial volume
    useEffect(() => {
      audioRef.current.volume = volume / 100;
    }, [volume]);

    // Audio event listeners
    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handleEnded = () => {
        if (isRepeat) {
          audio.currentTime = 0;
          audio
            .play()
            .catch(err => console.error('Play error after end:', err));
        } else {
          handleNext();
        }
      };

      const updateProgress = () => {
        if (!isSeeking && audio.duration) {
          const progressPercent = (audio.currentTime / audio.duration) * 100;
          setProgress(progressPercent);
          setCurrentTime(audio.currentTime);
          setDuration(audio.duration);
        }
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('timeupdate', updateProgress);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('timeupdate', updateProgress);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }, [isRepeat, handleNext, isSeeking]);

    // Handle track changes and playback
    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const shouldLoadNewTrack =
        currentTrackIndex !== null &&
        playlist[currentTrackIndex] &&
        !isLoadingAudio &&
        (lastPlayedTrackRef.current !==
          `${currentTrackIndex}-${playlist[currentTrackIndex].id}` ||
          shouldPlayRef.current);

      if (shouldLoadNewTrack) {
        shouldPlayRef.current = false;
        lastPlayedTrackRef.current = `${currentTrackIndex}-${playlist[currentTrackIndex].id}`;

        if (audio.src) {
          audio.pause();
        }

        const loadAndPlayAudio = async () => {
          setIsLoadingAudio(true);
          try {
            playbackUrlRef.current = playlist[currentTrackIndex].signed_url;
            audio.src = playbackUrlRef.current;

            // Wait for audio to be loadable
            await new Promise(resolve => {
              let isCleaned = false;

              const cleanup = () => {
                if (isCleaned) return;
                isCleaned = true;

                audio.removeEventListener('canplaythrough', loadHandler);
                audio.removeEventListener('error', errorHandler);
                clearTimeout(timeoutId);
              };

              const loadHandler = () => {
                cleanup();
                resolve(null);
              };

              const errorHandler = () => {
                cleanup();
                resolve(null);
              };

              const timeoutId = setTimeout(() => {
                cleanup();
                resolve(null);
              }, 10000); // 10 seconds timeout

              audio.addEventListener('canplaythrough', loadHandler);
              audio.addEventListener('error', errorHandler);
            });

            try {
              await audio.play();
              setIsPlaying(true);

              // If sidebar is closed, show mini player
              if (!isSidebarOpen) {
                setMiniPlayerVisible(true);
              }
            } catch (playError) {
              console.error('Error playing audio:', playError);
              setIsPlaying(false);
              setError('Error playing podcast. Try again or refresh the page.');
              setTimeout(() => setError(null), 5000);
            }
          } catch (e) {
            console.error('Error in loadAndPlayAudio:', e);
            setIsPlaying(false);
          } finally {
            setIsLoadingAudio(false);
          }
        };

        loadAndPlayAudio();
      }

      // return () => {
      //   if (audio && audio.src) {
      //     audio.pause();
      //   }
      // };
    }, [currentTrackIndex, playlist, isLoadingAudio, isSidebarOpen]);

    // Handle click outside sidebar to close it
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          sidebarRef.current &&
          !sidebarRef.current.contains(event.target as Node) &&
          isSidebarOpen
        ) {
          // Don't close if clicking on the mini-player
          const target = event.target as HTMLElement;
          if (target.closest('.mini-player')) return;

          toggleSidebar();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isSidebarOpen, toggleSidebar]);

    // Add a keyboard shortcut for spacebar to toggle play/pause
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Only trigger if no input elements are focused
        if (
          e.code === 'Space' &&
          document.activeElement?.tagName !== 'INPUT' &&
          document.activeElement?.tagName !== 'TEXTAREA' &&
          document.activeElement?.tagName !== 'BUTTON'
        ) {
          e.preventDefault();
          togglePlayPause();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [togglePlayPause]);

    // Playlist management with animation
    const addToPlaylist = async (podcast: Podcast) => {
      // Don't add if it's already in the playlist
      if (playlist.some(item => item.id === podcast.id)) return;

      setIsAdding(true);

      // For non-Bienvenida podcasts, first get a fresh URL
      if (podcast.id !== WELCOME_PODCAST_ID && !isLoadingAudio) {
        try {
          setIsLoadingAudio(true);
          const freshUrlData = await ApiStore.getAudioSignedUrl(podcast.id);

          const podcastWithFreshUrl = {
            ...podcast,
            signed_url: freshUrlData.signed_url,
          };

          setPlaylist(prev => [...prev, podcastWithFreshUrl]);
        } catch (error) {
          console.error('Error getting fresh URL for add to playlist:', error);
          setPlaylist(prev => [...prev, podcast]);
        } finally {
          setIsLoadingAudio(false);
          setIsAdding(false);
        }
      } else {
        setPlaylist(prev => [...prev, podcast]);
        setTimeout(() => setIsAdding(false), 300);
      }

      setIsSidebarOpen(true);
      setMiniPlayerVisible(false);
    };

    const removeFromPlaylist = (podcastId: number) => {
      const newPlaylist = playlist.filter(item => item.id !== podcastId);
      setPlaylist(newPlaylist);

      if (newPlaylist.length === 0) {
        handleStop();
        setCurrentTrackIndex(null);
        setMiniPlayerVisible(false);
      } else if (
        currentTrackIndex !== null &&
        currentTrackIndex >= newPlaylist.length
      ) {
        setCurrentTrackIndex(newPlaylist.length - 1);
      }
    };

    // Playback progress handling
    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newProgress = (clickX / rect.width) * 100;
      setProgress(newProgress);
      audioRef.current.currentTime = (newProgress / 100) * duration;
      setCurrentTime(audioRef.current.currentTime);
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      let clientX = e.clientX;

      // Ensure clientX is within the progress bar
      if (clientX < rect.left) clientX = rect.left;
      if (clientX > rect.right) clientX = rect.right;

      const clickX = clientX - rect.left;
      const newProgress = (clickX / rect.width) * 100;
      setProgress(newProgress);
      audioRef.current.currentTime = (newProgress / 100) * duration;
      setCurrentTime(audioRef.current.currentTime);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      setIsSeeking(true);
      handleSeek(e);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (isSeeking) {
        handleSeek(e);
      }
    };

    const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
      if (isSeeking) {
        handleSeek(e);
        setIsSeeking(false);
      }
    };

    // Filter podcasts based on search term and category
    const filteredPodcasts = podcasts.filter(podcast => {
      const matchesSearch =
        podcast.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (podcast.topic &&
          podcast.topic.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory =
        activeCategory === 'Todos' || podcast.category === activeCategory;

      return matchesSearch && matchesCategory;
    });

    // Play a podcast directly
    const playPodcast = async (podcast: Podcast) => {
      if (
        currentTrackIndex !== null &&
        playlist[currentTrackIndex]?.id === podcast.id
      ) {
        togglePlayPause();
        return;
      }

      // For non-Bienvenida podcasts, first get a fresh URL
      if (podcast.id !== WELCOME_PODCAST_ID && !isLoadingAudio) {
        try {
          setIsLoadingAudio(true);
          const freshUrlData = await ApiStore.getAudioSignedUrl(podcast.id);
          const podcastWithFreshUrl = {
            ...podcast,
            signed_url: freshUrlData.signed_url,
          };
          setPlaylist([podcastWithFreshUrl]);
        } catch (error) {
          console.error('Error getting fresh URL for play:', error);
          setPlaylist([podcast]);
        } finally {
          setIsLoadingAudio(false);
        }
      } else {
        setPlaylist([podcast]);
      }

      setCurrentTrackIndex(0);
      setIsSidebarOpen(true);
      setMiniPlayerVisible(false);

      setTimeout(() => {
        forcePlayCurrentTrack();
      }, 100);
    };

    // Move a podcast in the playlist (for drag and drop)
    const reorderPlaylist = (dragIndex: number, dropIndex: number) => {
      if (dragIndex === dropIndex) return;

      const newPlaylist = [...playlist];
      const draggedItem = newPlaylist[dragIndex];

      // Remove the dragged item
      newPlaylist.splice(dragIndex, 1);

      // Insert at new position
      newPlaylist.splice(dropIndex, 0, draggedItem);

      setPlaylist(newPlaylist);

      // Update current track index if needed
      if (currentTrackIndex === dragIndex) {
        setCurrentTrackIndex(dropIndex);
      } else if (
        currentTrackIndex !== null &&
        ((dragIndex < currentTrackIndex && dropIndex >= currentTrackIndex) ||
          (dragIndex > currentTrackIndex && dropIndex <= currentTrackIndex))
      ) {
        setCurrentTrackIndex(
          currentTrackIndex + (dragIndex < dropIndex ? -1 : 1)
        );
      }
    };

    // Render loading and error states
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <RingLoader color="#000000" size={120} />
            <p className="mt-4 text-gray-700 font-medium">
              Cargando podcasts...
            </p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              onClick={() => window.location.reload()}
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        {/* Main Content */}
        <div
          className={`pb-20 ${isSidebarOpen ? 'pr-80' : ''} transition-all duration-${ANIMATION_DURATION}`}
        >
          <div className="p-8 max-w-6xl mx-auto">
            <h1 className="text-4xl font-bold mb-8 text-gray-800 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-800">
              Galería de Podcasts
            </h1>

            {/* Search Bar */}
            <div className="mb-8 relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Buscar podcasts..."
                className="w-full p-4 pr-12 rounded-full border-0 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <FiSearch className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            </div>

            {/* Category Filter */}
            <div className="mb-8 flex flex-wrap gap-3 justify-center">
              {CATEGORIES.map(category => (
                <button
                  key={category}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === category
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Podcast Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPodcasts.map(podcast => (
                <div
                  key={podcast.id}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
                  onMouseEnter={() => setHoveredPodcast(podcast.id)}
                  onMouseLeave={() => setHoveredPodcast(null)}
                >
                  <div className="relative h-48 overflow-hidden group">
                    <img
                      src={podcast.image}
                      alt={podcast.file_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {/* Overlay that appears on hover */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center`}
                    >
                      <div className="flex gap-4">
                        <button
                          className="p-3 rounded-full bg-white/90 text-gray-800 hover:bg-white transition-colors"
                          aria-label="Add to playlist"
                          onClick={e => {
                            e.stopPropagation();
                            addToPlaylist(podcast);
                          }}
                        >
                          <FiPlusCircle className="text-xl" />
                        </button>
                        <button
                          className="p-3 rounded-full bg-blue-500/90 text-white hover:bg-blue-500 transition-colors"
                          aria-label="Play podcast"
                          onClick={e => {
                            e.stopPropagation();
                            playPodcast(podcast);
                          }}
                        >
                          {isPlaying &&
                          currentTrackIndex !== null &&
                          playlist[currentTrackIndex]?.id === podcast.id ? (
                            <FiPause className="text-xl" />
                          ) : (
                            <FiPlay className="text-xl" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Duration badge */}
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center">
                      <FiClock className="mr-1" />
                      {formatTime(podcast.duration)}
                    </div>

                    {/* Category badge */}
                    <div className="absolute top-2 left-2 bg-blue-500/90 text-white text-xs px-2 py-1 rounded-full">
                      {podcast.category}
                    </div>
                  </div>

                  <div className="p-5">
                    <h2 className="text-lg font-semibold mb-1 text-gray-800">
                      {podcast.title}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {podcast.description || 'No description available'}
                    </p>

                    <div className="flex justify-between items-center">
                      <button
                        className={`text-sm font-medium flex items-center ${
                          isPlaying &&
                          currentTrackIndex !== null &&
                          playlist[currentTrackIndex]?.id === podcast.id
                            ? 'text-blue-500'
                            : 'text-gray-500 hover:text-blue-500'
                        } transition-colors`}
                        onClick={() => playPodcast(podcast)}
                      >
                        {isPlaying &&
                        currentTrackIndex !== null &&
                        playlist[currentTrackIndex]?.id === podcast.id ? (
                          <>
                            <FiPause className="mr-1" /> Pausar
                          </>
                        ) : (
                          <>
                            <FiPlay className="mr-1" /> Reproducir
                          </>
                        )}
                      </button>

                      <button
                        className={`text-sm font-medium flex items-center ${
                          playlist.some(item => item.id === podcast.id)
                            ? 'text-blue-500'
                            : 'text-gray-500 hover:text-blue-500'
                        } transition-colors ${isAdding ? 'animate-pulse' : ''}`}
                        onClick={() => addToPlaylist(podcast)}
                        disabled={isAdding}
                      >
                        {playlist.some(item => item.id === podcast.id) ? (
                          <>
                            <FiCheck className="mr-1" /> En playlist
                          </>
                        ) : (
                          <>
                            <FiPlusCircle className="mr-1" /> Añadir
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty state */}
            {filteredPodcasts.length === 0 && (
              <div className="mt-12 flex flex-col items-center justify-center text-center">
                <div className="bg-blue-50 rounded-full p-8 mb-4">
                  <FiSearch className="text-4xl text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No se encontraron podcasts
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  No hay podcasts que coincidan con tu búsqueda. Intenta con
                  otros términos o categorías.
                </p>
                <button
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('Todos');
                  }}
                >
                  Mostrar todos los podcasts
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar / Player */}
        <div
          ref={sidebarRef}
          className={`fixed right-0 top-0 h-full bg-white shadow-xl transition-all duration-${ANIMATION_DURATION} ease-in-out z-40 overflow-hidden ${
            isSidebarOpen ? 'w-80' : 'w-0'
          }`}
        >
          {isSidebarOpen && (
            <div className="h-full flex flex-col">
              {/* Sidebar Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold">Tu Playlist</h2>
                  <button
                    onClick={toggleSidebar}
                    className="p-1 rounded-full hover:bg-white/20 transition-colors"
                    aria-label="Close playlist"
                  >
                    <FiChevronRight className="text-xl" />
                  </button>
                </div>

                {currentTrackIndex !== null && playlist[currentTrackIndex] && (
                  <div className="mt-5 flex space-x-4">
                    <img
                      src={playlist[currentTrackIndex].image}
                      alt={playlist[currentTrackIndex].title}
                      className="w-16 h-16 rounded-md object-cover shadow-md"
                    />
                    <div className="flex-grow overflow-hidden">
                      <h3 className="font-medium text-white mb-1 truncate">
                        {playlist[currentTrackIndex].title}
                      </h3>
                      <p className="text-sm text-blue-100 truncate">
                        {playlist[currentTrackIndex].category}
                      </p>
                      <div className="flex items-center mt-2">
                        <span className="text-xs">
                          {formatTime(currentTime)}
                        </span>
                        <div className="flex-grow mx-2 bg-white/30 h-1 rounded-full">
                          <div
                            className="bg-white h-1 rounded-full"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs">{formatTime(duration)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Playlist Items */}
              <div className="flex-grow overflow-y-auto custom-scrollbar">
                {playlist.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                      <FiList className="text-3xl text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      Tu playlist está vacía
                    </h3>
                    <p className="text-gray-500 text-sm">
                      Añade podcasts a tu playlist para comenzar a escucharlos.
                    </p>
                  </div>
                ) : (
                  <ul className="py-4">
                    {playlist.map((podcast, index) => (
                      <li
                        key={`${podcast.id}-${index}`}
                        className={`px-6 py-3 flex items-center justify-between cursor-pointer transition-colors ${
                          currentTrackIndex === index
                            ? 'bg-blue-50 border-l-4 border-blue-500'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                        onClick={() => {
                          setCurrentTrackIndex(index);
                          forcePlayCurrentTrack();
                        }}
                        draggable
                        onDragStart={e =>
                          e.dataTransfer.setData('text/plain', index.toString())
                        }
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                          e.preventDefault();
                          const dragIndex = parseInt(
                            e.dataTransfer.getData('text/plain')
                          );
                          reorderPlaylist(dragIndex, index);
                        }}
                      >
                        <div className="flex items-center flex-grow min-w-0">
                          <div className="relative flex-shrink-0">
                            <img
                              src={podcast.image}
                              alt={podcast.title}
                              className="w-12 h-12 object-cover rounded-md"
                            />
                            {currentTrackIndex === index && isPlaying && (
                              <div className="absolute inset-0 bg-black/40 rounded-md flex items-center justify-center">
                                <div className="equalizer">
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="ml-3 flex-grow min-w-0">
                            <h3
                              className={`font-medium text-sm truncate ${currentTrackIndex === index ? 'text-blue-600' : 'text-gray-800'}`}
                            >
                              {podcast.title}
                            </h3>
                            <div className="flex items-center text-xs text-gray-500 mt-1">
                              <span className="truncate">
                                {podcast.category}
                              </span>
                              <span className="mx-1">•</span>
                              <span>{formatTime(podcast.duration)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center ml-2">
                          {currentTrackIndex === index && isPlaying ? (
                            <button
                              className="p-1.5 rounded-full text-blue-500 hover:bg-blue-50 transition-colors"
                              onClick={e => {
                                e.stopPropagation();
                                togglePlayPause();
                              }}
                            >
                              <FiPause className="text-sm" />
                            </button>
                          ) : (
                            <button
                              className="p-1.5 rounded-full text-gray-600 hover:bg-gray-100 hover:text-blue-500 transition-colors"
                              onClick={e => {
                                e.stopPropagation();
                                setCurrentTrackIndex(index);
                                setIsPlaying(true);
                                forcePlayCurrentTrack();
                              }}
                            >
                              <FiPlay className="text-sm" />
                            </button>
                          )}
                          <button
                            className="p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              removeFromPlaylist(podcast.id);
                            }}
                          >
                            <FiTrash2 className="text-sm" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Playback Controls */}
              <div className="bg-gray-50 border-t border-gray-200 p-5">
                {/* Progress Bar */}
                <div
                  className="bg-gray-200 h-2 rounded-full relative cursor-pointer mb-4"
                  onClick={handleProgressClick}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={() => isSeeking && setIsSeeking(false)}
                >
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${progress}%` }}
                  ></div>
                  <div
                    className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full cursor-pointer shadow-md"
                    style={{
                      left: `${progress}%`,
                      display: isSeeking || progress > 0 ? 'block' : 'none',
                    }}
                  ></div>
                </div>

                {/* Volume Control */}
                <div className="flex items-center mb-4">
                  <button
                    className="p-2 text-gray-600 hover:text-blue-500 transition-colors focus:outline-none"
                    onClick={toggleMute}
                  >
                    {isMuted ? <FiVolumeX /> : <FiVolume2 />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-full h-2 appearance-none bg-gray-200 rounded-full mx-2 focus:outline-none"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${isMuted ? 0 : volume}%, #e5e7eb ${isMuted ? 0 : volume}%, #e5e7eb 100%)`,
                    }}
                  />
                </div>

                {/* Control Buttons */}
                <div className="flex justify-between items-center">
                  <button
                    className={`p-2 rounded-full ${
                      isShuffle
                        ? 'text-blue-500 bg-blue-50'
                        : 'text-gray-600 hover:text-blue-500'
                    }`}
                    onClick={toggleShuffle}
                    aria-label="Shuffle"
                  >
                    <FiShuffle />
                  </button>

                  <div className="flex items-center space-x-4">
                    <button
                      className="p-2 text-gray-600 hover:text-blue-500 transition-colors"
                      onClick={handlePrevious}
                      aria-label="Previous track"
                      disabled={playlist.length === 0}
                    >
                      <FiSkipBack />
                    </button>

                    <button
                      className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors flex items-center justify-center shadow-md"
                      onClick={togglePlayPause}
                      aria-label={isPlaying ? 'Pause' : 'Play'}
                      disabled={playlist.length === 0}
                    >
                      {isPlaying ? (
                        <FiPause className="text-lg" />
                      ) : (
                        <FiPlay className="text-lg" />
                      )}
                    </button>

                    <button
                      className="p-2 text-gray-600 hover:text-blue-500 transition-colors"
                      onClick={handleNext}
                      aria-label="Next track"
                      disabled={playlist.length === 0}
                    >
                      <FiSkipForward />
                    </button>
                  </div>

                  <button
                    className={`p-2 rounded-full ${
                      isRepeat
                        ? 'text-blue-500 bg-blue-50'
                        : 'text-gray-600 hover:text-blue-500'
                    }`}
                    onClick={toggleRepeat}
                    aria-label="Repeat"
                  >
                    <FiRepeat />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mini Player */}
        {miniPlayerVisible &&
          currentTrackIndex !== null &&
          playlist[currentTrackIndex] && (
            <MiniPlayer
              currentPodcast={playlist[currentTrackIndex]}
              isPlaying={isPlaying}
              progress={progress}
              togglePlayPause={togglePlayPause}
              toggleSidebar={toggleSidebar}
            />
          )}

        {/* Hidden Audio Element */}
        <audio ref={audioRef} />

        {/* CSS for audio visualizer */}
        <style jsx>{`
          /* Custom scrollbar */
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #c5c7d0;
            border-radius: 10px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #a1a3b0;
          }

          /* Audio visualizer */
          .equalizer {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 20px;
          }

          .equalizer span {
            display: inline-block;
            width: 3px;
            height: 100%;
            margin: 0 1px;
            background-color: #fff;
            border-radius: 3px;
            animation: equalize 1.2s steps(10, end) infinite;
          }

          .equalizer span:nth-child(1) {
            animation-delay: -1.2s;
            height: 30%;
          }

          .equalizer span:nth-child(2) {
            animation-delay: -0.9s;
            height: 60%;
          }

          .equalizer span:nth-child(3) {
            animation-delay: -0.6s;
            height: 40%;
          }

          @keyframes equalize {
            0% {
              height: 30%;
            }
            10% {
              height: 50%;
            }
            20% {
              height: 70%;
            }
            30% {
              height: 40%;
            }
            40% {
              height: 80%;
            }
            50% {
              height: 50%;
            }
            60% {
              height: 30%;
            }
            70% {
              height: 70%;
            }
            80% {
              height: 50%;
            }
            90% {
              height: 40%;
            }
            100% {
              height: 60%;
            }
          }

          /* Range input styling */
          input[type='range'] {
            -webkit-appearance: none;
            height: 4px;
            border-radius: 4px;
          }

          input[type='range']::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 14px;
            height: 14px;
            background: #3b82f6;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          }

          input[type='range']::-moz-range-thumb {
            width: 14px;
            height: 14px;
            background: #3b82f6;
            border-radius: 50%;
            cursor: pointer;
            border: none;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          }

          /* Drag and drop hint */
          li[draggable='true']:hover::after {
            content: '⋮';
            position: absolute;
            left: 0;
            color: #9ca3af;
            font-size: 16px;
            font-weight: bold;
          }
        `}</style>
      </div>
    );
  })
);

export default PodcastList;
